import { Link } from '@tanstack/react-router';
import { BellOff, KeyRound, ScanFace, Smartphone } from 'lucide-react';
import type { AdminCompanyStaff } from 'app/javascript/types/admin';
import { Avatar, AvatarFallback } from 'app/javascript/components/ui/avatar';
import { Badge } from 'app/javascript/components/ui/badge';
import UserDevices from 'app/javascript/components/admin/UserDevices';
import { ROUTES } from 'app/javascript/constants/routes';
import { formatBRDateTime } from 'app/javascript/utils/dates';
import { buildWhatsAppUrl } from 'app/javascript/utils/whatsapp';

const ROLE_LABELS: Record<string, string> = { owner: 'Dono', admin: 'Admin' };

function initials(name: string | null) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

// One person who runs the academy. The badges on the right are the diagnosis:
// each one is a reason this person may not be able to use the app. Nothing is
// shown when everything is in order — a row without badges reads as fine.
export default function StaffRow({ member }: { member: AdminCompanyStaff }) {
  const whatsappUrl = member.lgpd_erased ? null : buildWhatsAppUrl(member.phone, '');
  const blockers = [
    !member.claimed && { key: 'claimed', icon: KeyRound, label: 'conta não reivindicada' },
    !member.face_enrolled && { key: 'face', icon: ScanFace, label: 'sem facial' },
    member.push_devices === 0 && { key: 'push', icon: BellOff, label: 'sem push' },
  ].filter(Boolean) as { key: string; icon: typeof KeyRound; label: string }[];

  return (
    <div className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-x-4 gap-y-2 py-3 sm:flex sm:flex-wrap sm:items-center">
      <Avatar className="size-9">
        <AvatarFallback className="bg-secondary text-xs font-semibold">{initials(member.name)}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{member.name ?? '—'}</span>
          <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>{ROLE_LABELS[member.role] ?? member.role}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="truncate">{member.email ?? 'sem e-mail'}</span>
          {member.slug && !member.lgpd_erased && (
            <Link
              to={ROUTES.PUBLIC_PROFILE}
              params={{ slug: member.slug }}
              target="_blank"
              rel="noreferrer noopener"
              className="hover:text-foreground hover:underline"
            >
              /p/{member.slug}
            </Link>
          )}
        </div>
        <p className="mt-1 break-words text-xs text-muted-foreground">
          {whatsappUrl ? (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm text-yellow-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50"
            >
              WhatsApp pessoal: +{member.phone}
            </a>
          ) : member.phone ? (
            `Telefone pessoal: ${member.phone} (confira o número)`
          ) : (
            'WhatsApp pessoal não cadastrado'
          )}
          {member.phone && (
            <span className="block">
              {member.whatsapp_opt_in_at
                ? `Autorizou avisos transacionais em ${formatBRDateTime(member.whatsapp_opt_in_at)}`
                : 'Sem autorização registrada para avisos transacionais'}
            </span>
          )}
        </p>
        {member.devices && member.devices.length > 0 && (
          <div className="mt-1">
            <UserDevices devices={member.devices} />
          </div>
        )}
      </div>

      <div className="col-start-2 flex flex-wrap items-center gap-1.5">
        {blockers.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
            <Smartphone className="size-3.5" />
            pronto para usar
          </span>
        ) : (
          blockers.map(({ key, icon: Icon, label }) => (
            <Badge key={key} variant="outline" className="border-amber-500/40 text-amber-400">
              <Icon className="size-3" />
              {label}
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}
